/* The transition plan (EXP-20).
   **Expected and achieved are side by side and never one column.** The adjacency is the feature: a
   projection and a measurement summed is a plan that reads as a result, and the gap between them —
   which is what a reader actually wants — is exactly what the sum destroys. */

.stack {
  grid-column: 1 / -1;
  display: grid;
  gap: var(--gap);
  min-width: 0;
}

.tiles {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: var(--gap);
  min-width: 0;
}

/* Two panels side by side, one below a laptop. `align-items: start` so a short panel is not
   stretched to the height of a long one. */
.pair2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--gap);
  align-items: start;
  min-width: 0;
}

.single {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--gap);
  min-width: 0;
}

@media (width <= 1200px) {
  .tiles {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (width <= 1100px) {
  .pair2 {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (width <= 760px) {
  .tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (width <= 480px) {
  .tiles {
    grid-template-columns: minmax(0, 1fr);
  }
}

.sentence {
  margin: 0 0 10px;
  font-size: var(--t-dense);
  line-height: 1.5;
  color: var(--ink);
}

.facts {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 3px 12px;
  font-size: var(--t-min);
  line-height: 1.5;
}

.k {
  color: var(--muted);
}

.v {
  color: var(--ink-2);
}

/* A number the product deliberately does not produce. Dashed, like every other "this is a limit of
   the product rather than a fact about your data" note in this app — a reader who has learned what
   that border means on the bulk screen has learned it here. */
.absent {
  margin: 12px 0 0;
  padding: 8px 10px;
  border: 1px dashed var(--line-2);
  border-radius: 7px;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.assumptions,
.initiatives,
.decisions {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
}

.assumption {
  display: grid;
  gap: 1px;
  font-size: var(--t-min);
}

.assumptionKey {
  font-weight: 600;
  color: var(--ink);
}

.assumptionValue {
  color: var(--ink-2);
}

/* The source. Quiet but never hidden: an assumption with no stated source is one nobody can argue
   with, so the place it is stated has to be visible. */
.assumptionSource {
  color: var(--muted);
}

.initiative {
  display: grid;
  gap: 6px;
  padding: 9px 11px;
  border: 1px solid var(--line);
  border-radius: 7px;
}

.initiativeName {
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
}

.pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.half {
  display: grid;
  gap: 1px;
  padding: 6px 8px;
  border-radius: 6px;
  background: var(--panel-3);
}

.halfLabel {
  font-size: var(--t-min);
  color: var(--muted);
}

.halfValue {
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
}

/* "modelled" / "no evidence yet". The basis travels with the figure, because a projection and a
   measurement that look identical are the failure EXP-37 names. */
.halfNote {
  font-size: var(--t-min);
  color: var(--muted);
}

.findings {
  margin-top: 10px;
}

.finding {
  margin: 0 0 6px;
  padding: 8px 10px;
  border: 1px solid var(--warn-line);
  border-radius: 7px;
  background: var(--warn-w);
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.decision {
  display: grid;
  gap: 1px;
  font-size: var(--t-min);
  line-height: 1.5;
}

.decisionWhen {
  color: var(--muted);
}

.decisionWhat {
  font-weight: 600;
  color: var(--ink);
}

.decisionWhy {
  color: var(--ink-2);
}

.form {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid var(--line-2);
}

.inline {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* Two controls side by side where there is room and one above the other where there is not. A date
   input has an intrinsic width its flex item cannot shrink below on every platform (the CI browser's
   is wider than a laptop's), so "shrink to fit" widened the screen's body by 52px at 375px. A basis
   to wrap at, not a minimum to fight, is what lets the row break instead of overflow. */
.inline input {
  min-width: 0;
  flex: 1 1 9rem;
}

/* A role that cannot commit, told so rather than shown a control, and never shown nothing at all
   (design rule 6). An absent form reads as a product that has not built it; a sentence reads as one
   that knows whose decision it is. */
.unauthorized {
  margin: 10px 0 0;
  padding: 9px 11px;
  border: 1px solid var(--warn-line);
  border-radius: 7px;
  background: var(--warn-w);
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

/* The history of one target, and the projections of one set of assumptions. Both are lists of
   versions, newest or oldest first, each one a statement that stays what it was. */
.history {
  margin-top: 10px;
}

.versions {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  display: grid;
  gap: 8px;
}

.version,
.projection {
  display: grid;
  gap: 2px;
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: 7px;
  font-size: var(--t-min);
  line-height: 1.5;
}

.versionHead {
  font-weight: 600;
  color: var(--ink);
}

/* The toggle that draws a version of the assumptions on the pathway. */
.drawButton {
  justify-self: start;
  min-height: var(--target-min);
  padding: 2px 10px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-pill);
  background: var(--panel);
  color: var(--ink);
  font: inherit;
  font-size: var(--t-min);
  cursor: pointer;
}

.drawButton:hover {
  background: var(--panel-2);
}

.drawButton[aria-pressed="true"] {
  background: var(--info-w);
  border-color: var(--info);
  color: var(--info);
  font-weight: 600;
}

.drawButton:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 1px;
}

/* The row of actions beside a figure, and the one-sentence empty state of a list. */
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.empty {
  margin: 0;
  color: var(--muted);
}
